import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Users, UserCheck, UserX, UserPlus, Layers, Dumbbell, ArrowRight, Activity, CalendarClock, Gift, Music, HeartPulse, BarChart3 } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState({ clients: [], programs: [], exercises: [] });
  const [extra, setExtra] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [savingPlaylist, setSavingPlaylist] = useState(false);

  useEffect(() => {
    api.get("/settings/spotify-playlist")
      .then((r) => setPlaylistUrl(r.data.playlist_id ? `https://open.spotify.com/playlist/${r.data.playlist_id}` : ""))
      .catch(() => {});
  }, []);

  const savePlaylist = async () => {
    setSavingPlaylist(true);
    try {
      const r = await api.put("/settings/spotify-playlist", { playlist_url: playlistUrl });
      setPlaylistUrl(r.data.playlist_id ? `https://open.spotify.com/playlist/${r.data.playlist_id}` : "");
      toast.success(t("coach.spotifySaved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingPlaylist(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/coach/clients").catch(() => ({ data: [] })),
      api.get("/programs").catch(() => ({ data: [] })),
      api.get("/exercises").catch(() => ({ data: [] })),
      api.get("/coach/stats").catch(() => ({ data: {} })),
      api.get("/coach/analytics").catch(() => ({ data: {} })),
    ]).then(([c, p, e, s, a]) => {
      setStats({ clients: c.data, programs: p.data, exercises: e.data });
      setExtra(s.data || {});
      setAnalytics(a.data || {});
    });
  }, []);

  const active = stats.clients.filter((c) => !c.blocked).length;
  const expired = stats.clients.filter((c) => c.blocked).length;

  const cards = [
    { icon: Users, label: t("coach.totalClients"), value: stats.clients.length, testid: "stat-total-clients", to: "/coach/clients" },
    { icon: UserCheck, label: t("coach.activeClients"), value: active, testid: "stat-active-clients", color: "text-green-500", to: "/coach/clients" },
    { icon: UserX, label: t("coach.expiredClients"), value: expired, testid: "stat-expired-clients", color: "text-destructive", to: "/coach/clients" },
    { icon: Layers, label: t("coach.programsCount"), value: stats.programs.length, testid: "stat-programs", color: "text-accent", to: "/coach/programs" },
    { icon: Dumbbell, label: t("coach.exercisesCount"), value: stats.exercises.length, testid: "stat-exercises", color: "text-amber-500", to: "/coach/exercises" },
    { icon: Activity, label: t("coach.workoutsWeek"), value: extra.workouts_week ?? 0, testid: "stat-workouts-week", color: "text-primary", to: "/coach/clients" },
    { icon: CalendarClock, label: t("coach.expiring7d"), value: extra.expiring_7d ?? 0, testid: "stat-expiring-7d", color: "text-destructive", to: "/coach/clients" },
    { icon: Gift, label: t("coach.referralsCount"), value: extra.total_referrals ?? 0, testid: "stat-referrals", color: "text-accent", to: "/coach/promos" },
  ];

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard"), end: true },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/promos", key: "promos", label: t("nav.promos") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  return (
    <Layout nav={nav}>
      <div data-testid="coach-dashboard">
        <p className="kc-eyebrow">{t("coach.welcome")}</p>
        <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mb-8">{user?.name}</h1>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
          {cards.map((c) => (
            <Link key={c.label} to={c.to} className="kc-card kc-card-hover p-5 block" data-testid={c.testid}>
              <c.icon className={`w-6 h-6 mb-3 ${c.color || "text-primary"}`} />
              <p className="font-mono text-3xl sm:text-4xl font-black tracking-tighter">{c.value}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{c.label}</p>
            </Link>
          ))}
        </div>
        <div className="kc-card p-6 mb-10" data-testid="health-overview">
          <p className="kc-eyebrow flex items-center gap-2 mb-4"><HeartPulse className="w-4 h-4" />{t("coach.healthTitle")}</p>
          <div className="flex flex-wrap gap-3">
            <span className="kc-badge bg-green-500/10 text-green-700 border border-green-500/30" data-testid="health-green">
              <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5" />{extra.health?.green ?? 0} · {t("coach.healthOk")}
            </span>
            <span className="kc-badge bg-amber-400/10 text-amber-600 border border-amber-400/40" data-testid="health-yellow">
              <span className="w-2 h-2 rounded-full bg-amber-400 mr-1.5" />{extra.health?.yellow ?? 0} · {t("coach.healthWarn")}
            </span>
            <span className="kc-badge bg-destructive/10 text-destructive border border-destructive/30" data-testid="health-red">
              <span className="w-2 h-2 rounded-full bg-destructive mr-1.5" />{extra.health?.red ?? 0} · {t("coach.healthRisk")}
            </span>
            <span className="kc-badge bg-secondary text-muted-foreground border border-border" data-testid="health-pending">
              <span className="w-2 h-2 rounded-full bg-muted-foreground/50 mr-1.5" />{extra.health?.pending ?? 0} · {t("coach.healthPending")}
            </span>
          </div>
          {(extra.health_attention || []).length > 0 ? (
            <div className="divide-y divide-border mt-4">
              {extra.health_attention.slice(0, 5).map((a) => (
                <Link key={a.client_id} to={`/coach/clients/${a.client_id}`} data-testid={`health-attention-${a.client_id}`}
                  className="flex items-center justify-between gap-4 py-2.5 group">
                  <span className="flex items-center gap-2 font-semibold truncate">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${a.health_status === "red" ? "bg-destructive" : "bg-amber-400"}`} />
                    {a.name}
                  </span>
                  <ArrowRight className="w-4 h-4 text-primary shrink-0 group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-4" data-testid="health-all-ok">{t("coach.healthAllOk")}</p>
          )}
        </div>
        {analytics && (
          <div className="kc-card p-6 mb-10" data-testid="analytics-overview">
            <p className="kc-eyebrow flex items-center gap-2 mb-4"><BarChart3 className="w-4 h-4" />{t("stats.visitsTitle")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
              {[
                { v: analytics.landing_today, l: t("stats.landingToday"), tid: "stat-landing-today" },
                { v: analytics.landing_week, l: t("stats.landingWeek"), tid: "stat-landing-week" },
                { v: analytics.landing_total, l: t("stats.landingTotal"), tid: "stat-landing-total" },
                { v: analytics.app_week, l: t("stats.appWeek"), tid: "stat-app-week" },
                { v: analytics.new_clients_week, l: t("stats.newClients"), tid: "stat-new-clients" },
              ].map((x) => (
                <div key={x.tid} className="text-center" data-testid={x.tid}>
                  <p className="text-2xl font-black">{x.v ?? 0}</p>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide mt-0.5">{x.l}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{t("stats.sources")}</p>
                {(analytics.referrers || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="sources-empty">{t("stats.noData")}</p>
                ) : (
                  <div className="space-y-1.5" data-testid="sources-list">
                    {analytics.referrers.map((r) => (
                      <div key={r.source} className="flex justify-between text-sm" data-testid={`source-${r.source}`}>
                        <span className="truncate">{r.source === "direct" ? t("stats.direct") : r.source}</span>
                        <span className="font-bold ml-3">{r.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{t("stats.recentAccess")}</p>
                {(analytics.recent_users || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="recent-empty">{t("stats.noData")}</p>
                ) : (
                  <div className="space-y-1.5" data-testid="recent-users-list">
                    {analytics.recent_users.map((u) => (
                      <div key={u.name + u.ts} className="flex justify-between gap-3 text-sm">
                        <span className="truncate">{u.name}</span>
                        <span className="text-muted-foreground font-mono text-xs shrink-0">{u.ts?.slice(5, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link data-testid="quick-clients-link" to="/coach/clients" className="kc-card kc-card-hover p-6 flex items-center justify-between group">
            <span className="font-bold">{t("nav.clients")}</span>
            <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link data-testid="quick-programs-link" to="/coach/programs" className="kc-card kc-card-hover p-6 flex items-center justify-between group">
            <span className="font-bold">{t("nav.programs")}</span>
            <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link data-testid="quick-exercises-link" to="/coach/exercises" className="kc-card kc-card-hover p-6 flex items-center justify-between group">
            <span className="font-bold">{t("nav.exercises")}</span>
            <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link data-testid="quick-new-coach-link" to="/register-coach" className="kc-card kc-card-hover p-6 flex items-center justify-between group">
            <span className="font-bold flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />{t("register.newCoach")}</span>
            <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="kc-card p-6 mt-10" data-testid="spotify-playlist-settings">
          <p className="kc-eyebrow flex items-center gap-2 mb-2"><Music className="w-4 h-4" />{t("coach.spotifyTitle")}</p>
          <p className="text-sm text-muted-foreground mb-4">{t("coach.spotifyHint")}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input data-testid="spotify-playlist-input" className="kc-input flex-1" placeholder="https://open.spotify.com/playlist/…"
              value={playlistUrl} onChange={(e) => setPlaylistUrl(e.target.value)} />
            <button data-testid="spotify-playlist-save" onClick={savePlaylist} disabled={savingPlaylist} className="kc-btn-primary !px-5">
              {savingPlaylist ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </div>

        <div className="kc-card p-6 mt-10" data-testid="referral-leaderboard">
          <p className="kc-eyebrow flex items-center gap-2 mb-4"><Gift className="w-4 h-4" />{t("coach.referralTitle")}</p>
          {(extra.referrals || []).length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="referral-empty">{t("coach.referralEmpty")}</p>
          ) : (
            <div className="divide-y divide-border">
              {extra.referrals.map((r) => (
                <div key={r.client_id} className="flex items-center justify-between gap-4 py-3" data-testid={`referral-row-${r.client_id}`}>
                  <div className="min-w-0">
                    <p className="font-bold truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono font-black text-primary" data-testid={`referral-invited-${r.client_id}`}>
                      {r.invited} <span className="text-xs font-normal text-muted-foreground">{t("ref.invited")}</span>
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid={`referral-credits-${r.client_id}`}>
                      {t("ref.creditsCount")}: {r.credits} (−{r.credits * 20}€)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
